import { WarningIcon } from "@phosphor-icons/react/Warning";
import type { Hours } from "@freightsimple/generated-dashboard-openapi-client/models/Hours";
import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";
import { describeDateRelative } from "@/Helpers/describeDateRelative";
import { describeDateRelativeWithTime } from "@/Helpers/describeDateRelativeWithTime";
import { describeHours } from "@/Helpers/describeHours";
import { formatPrice } from "@/Helpers/formatPrice";
import { priceBeforeTax } from "@/Helpers/priceBeforeTax";

import moment from "moment";
import type { DashboardRowTemplateBaseProps } from "@/Shared/Shipment/CustomComponents/ShipmentRowTemplate";
import ShipmentRowTemplate from "@/Shared/Shipment/CustomComponents/ShipmentRowTemplate";
import { colorForShipmentState } from "@/Helpers/colorForShipmentState";
import { shipmentRowNameForShipmentState } from "@/Shared/Shipment/Helpers/shipmentRowNameForShipmentState";
import { timeLabelForShipmentStateVerbose } from "@/Helpers/timeLabelForShipmentState";
import { ShipmentRowTimeValue } from "@/Shared/Shipment/CustomComponents/ShipmentRowTimeValue";
import { ShipmentRowDeleteAction } from "@/Shared/Shipment/CustomComponents/ShipmentRowDeleteAction";

function MissingCustomsDocsWarning_() {
  return (
    <div className="flex w-[200px] flex-row items-center">
      <WarningIcon className="mr-2 ml-8 h-5 w-5 text-gold" weight="fill" />
      <div className="text-[14px] text-content-muted">Missing customs docs</div>
    </div>
  );
}

function makeTimeDescription(
  shipmentState: ShipmentState,
  pickupDateDescription: string | undefined,
  expectedDeliveryDateDescription: string | undefined,
  appointmentDescription: string | undefined,
  actualDeliveryDateDescription: string | undefined,
): string {
  switch (shipmentState) {
    case ShipmentState.Quoted:
    case ShipmentState.QuoteRequested:
    case ShipmentState.BookingConfirmed:
    case ShipmentState.BookingRequested:
    case ShipmentState.BookingFailed:
      if (pickupDateDescription === undefined) {
        return "Unknown";
      }
      return pickupDateDescription;
    case ShipmentState.InTransit:
      if (appointmentDescription !== undefined) {
        return appointmentDescription;
      } else if (expectedDeliveryDateDescription === undefined) {
        return "Waiting for update";
      } else {
        return expectedDeliveryDateDescription;
      }

    case ShipmentState.Delivered:
      if (actualDeliveryDateDescription === undefined) {
        return "Waiting for update";
      }
      return actualDeliveryDateDescription;
    case ShipmentState.Cancelled:
      return "";
    case ShipmentState.Lost:
      return "";
    case ShipmentState.OnHold:
      return "On Hold";
  }
}

interface ShipmentDashboardRowProps extends DashboardRowTemplateBaseProps {
  shipmentState: ShipmentState;
  pickupDate: string | undefined;
  pickupHours: Hours | undefined;
  appointmentDate: string | undefined;
  expectedDeliveryDate: string | undefined;
  expectedDeliveryHours: string | undefined;
  actualDeliveryDate: string | undefined;
  actualDeliveryTime: string | undefined;
  selectedQuote?: Quote | undefined;
  cheapestQuote?: Quote | undefined;
  onDelete?: (() => Promise<void>) | undefined;
  overrideLastColumnLabel?: string | undefined;
  overrideLastColumnDescription?: string | undefined;
  needsCustomsDocs?: boolean | undefined;
  manualQuotingOpen: boolean;
  freightClaims?: boolean | undefined;
  bookedByName?: string | undefined;
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
function ShipmentRowBody(props: ShipmentDashboardRowProps) {
  const {
    shipmentState,
    pickupDate,
    pickupHours,
    appointmentDate,
    expectedDeliveryDate,
    expectedDeliveryHours,
    actualDeliveryDate,
    actualDeliveryTime,
    selectedQuote,
    cheapestQuote,
    onDelete,
    needsCustomsDocs,
    manualQuotingOpen,
    freightClaims,
  } = props;

  const pickupDateDescription = describeDateRelativeWithTime(
    pickupDate,
    pickupHours ? describeHours(pickupHours) : undefined,
    false,
  );

  const expectedDeliveryDateDescription = describeDateRelativeWithTime(
    expectedDeliveryDate,
    expectedDeliveryHours,
    true,
  );

  const appointmentDescription = appointmentDate
    ? describeDateRelative(appointmentDate)
    : undefined;

  const actualDeliveryTimeDescription = actualDeliveryTime
    ? moment(actualDeliveryTime, "HH:mm").format("h:mma")
    : undefined;

  const actualDeliveryDateDescription = describeDateRelativeWithTime(
    actualDeliveryDate,
    actualDeliveryTimeDescription,
    false,
  );

  const timeDescription = makeTimeDescription(
    shipmentState,
    pickupDateDescription,
    expectedDeliveryDateDescription,
    appointmentDescription,
    actualDeliveryDateDescription,
  );

  function getStateSubtitle() {
    if (shipmentState !== ShipmentState.Quoted) {
      return undefined;
    }

    if (selectedQuote) {
      return `Selected $${formatPrice(priceBeforeTax(selectedQuote.price, selectedQuote.taxAmount))}`;
    }

    if (cheapestQuote) {
      return `From $${formatPrice(priceBeforeTax(cheapestQuote.price, cheapestQuote.taxAmount))}`;
    }

    // We could be in a situation with no quotes even after it's quoted
    return undefined;
  }

  const stateSubtitle = getStateSubtitle();

  const displayDateAsError =
    (props.shipmentState === ShipmentState.Quoted ||
      props.shipmentState === ShipmentState.QuoteRequested ||
      props.shipmentState === ShipmentState.BookingFailed) &&
    pickupDate !== undefined &&
    moment(pickupDate).isBefore(moment().startOf("day"));

  const showTime =
    shipmentState !== ShipmentState.Cancelled &&
    shipmentState !== ShipmentState.Lost;

  return (
    <div className="mb-4">
      <div className="flex flex-row items-center">
        <ShipmentRowTemplate
          {...props}
          colorBar={colorForShipmentState(shipmentState)}
          state={
            <>
              {shipmentRowNameForShipmentState(
                shipmentState,
                selectedQuote,
                cheapestQuote,
                manualQuotingOpen,
              )}
              {freightClaims && (
                <span className="absolute -top-2 -right-4 mx-1 text-[24px] text-red">
                  <Tooltip
                    side="right"
                    content={
                      <div className="min-w-[200px] text-[14px] font-normal text-foreground">
                        This shipment has one or more freight claims
                      </div>
                    }
                  >
                    *
                  </Tooltip>
                </span>
              )}
            </>
          }
          stateSubtitle={stateSubtitle}
          carrierIdentifier={props.carrierIdentifier}
        >
          {showTime && (
            <div className="pl-8 in-[.dashboard-row-narrow]:pl-0">
              {props.overrideLastColumnLabel !== undefined && (
                <ShipmentRowTimeValue
                  label={props.overrideLastColumnLabel}
                  displayAsError={false}
                >
                  {props.overrideLastColumnDescription}
                </ShipmentRowTimeValue>
              )}
              {props.overrideLastColumnLabel === undefined && (
                <ShipmentRowTimeValue
                  label={timeLabelForShipmentStateVerbose(
                    shipmentState,
                    appointmentDescription,
                  )}
                  displayAsError={displayDateAsError}
                >
                  {timeDescription}
                </ShipmentRowTimeValue>
              )}
            </div>
          )}
        </ShipmentRowTemplate>

        {shipmentState === ShipmentState.Quoted && onDelete && (
          <ShipmentRowDeleteAction onClick={onDelete} />
        )}
        {shipmentState === ShipmentState.BookingConfirmed &&
          needsCustomsDocs && <MissingCustomsDocsWarning_ />}
      </div>
    </div>
  );
}
export default ShipmentRowBody;
